import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, Instagram, Wallet, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { IMG, LINKS, LOGO, PRESS, SCREENS } from "../data/site";
import { src, srcSet } from "../lib/img";
import { usePageMeta } from "../components/site/page";
import PressMarquee from "../components/site/PressMarquee";

const LINK_CARDS = [
  { id: "sutras", title: "Yoga Sutras: Presence Revealed", desc: "A meditation app inspired by the Yoga Sutras. Available now on the App Store.", to: LINKS.sutras, thumb: SCREENS[0].src.replace("1000w", "300w"), pos: "50% 30%", featured: true },
  { id: "practice-online", title: "Practice Online", desc: "Move, breathe and reconnect anytime.", to: LINKS.practiceOnline, img: IMG.devices },
  { id: "class-schedule", title: "Class Schedule", desc: "This week's classes in San Diego.", to: "/classes#this-week", img: IMG.indoorGroup },
  { id: "work-with-me", title: "Work With Me", desc: "Private sessions, massage & bodywork.", to: LINKS.workWithMe, img: IMG.bodywork },
  { id: "about", title: "About Jake", desc: "20+ years in yoga and wellness.", to: LINKS.bio, img: IMG.coastJake },
  { id: "contact", title: "Get in Touch", desc: "Questions, bookings and inquiries.", to: LINKS.contact, img: IMG.studioPrayer },
  { id: "retreats", title: "Retreats & Events", desc: "Workshops, events and retreats.", to: LINKS.retreats, img: IMG.shala },
];

const SOCIAL = [
  { id: "youtube", label: "YouTube", href: LINKS.youtube, Icon: Youtube },
  { id: "instagram", label: "Instagram", href: LINKS.instagram, Icon: Instagram },
  { id: "venmo", label: "Venmo", href: LINKS.venmo, Icon: Wallet },
];

const rise = (reduce, i) =>
  reduce ? {} : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.08 + i * 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

const PORTRAIT_STYLE = { width: "124%", left: "-32%", top: "13%" };

const OceanBand = ({ reduce }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], reduce ? [0, 0] : [0, 110]);
  return (
    <div aria-hidden className="connect-band pointer-events-none absolute inset-x-0 top-0 z-0 h-[660px] overflow-hidden sm:h-[760px]" data-testid="connect-ocean-band">
      <motion.img
        style={{ y, objectPosition: "50% 62%" }}
        src={src(IMG.coastline.p, 1500)}
        srcSet={srcSet(IMG.coastline.p, IMG.coastline.w)}
        sizes="100vw"
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-x-0 -top-[6%] h-[112%] w-full object-cover"
        data-testid="connect-ocean-photo"
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_58%_30%_at_50%_47%,rgba(16,26,31,0.66),rgba(16,26,31,0.3)_55%,rgba(16,26,31,0)_80%),linear-gradient(to_bottom,rgba(20,32,38,0.55)_0%,rgba(20,32,38,0.42)_38%,rgba(20,32,38,0.16)_62%,rgba(20,32,38,0)_80%)]" />
    </div>
  );
};

const Hero = ({ reduce }) => (
  <header className="relative z-10 px-6 pt-16 text-center text-ivory sm:pt-24" data-testid="connect-hero">
    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-[170px] -z-10 mx-auto h-[220px] max-w-[680px] rounded-full bg-[rgba(14,22,27,0.55)] blur-3xl sm:top-[200px]" data-testid="connect-hero-scrim" />
    <motion.div {...rise(reduce, 0)} className="mx-auto h-[139px] w-[139px] rounded-full bg-[linear-gradient(170deg,#F6C7A8,#EADFCF_75%)] p-[3px] shadow-[0_24px_44px_-22px_rgba(10,18,22,0.8)] sm:h-[157px] sm:w-[157px]">
      <div className="relative h-full w-full overflow-hidden rounded-full bg-[linear-gradient(170deg,#F2B294,#EFE2D2_80%)] ring-2 ring-white/85">
        <img
          src={src(IMG.portraitNavy.p, 500)}
          alt={IMG.portraitNavy.alt}
          width="500"
          height="750"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute h-auto max-w-none"
          style={PORTRAIT_STYLE}
          data-testid="connect-hero-photo"
        />
      </div>
    </motion.div>
    <motion.h1 {...rise(reduce, 1)} className="mt-7 font-display text-[2.1rem] font-medium leading-none tracking-[0.16em] [text-shadow:0_2px_20px_rgba(10,18,22,0.6),0_1px_2px_rgba(10,18,22,0.35)] sm:text-[2.6rem]" data-testid="connect-name">
      JAKE FERREE
    </motion.h1>
    <motion.p {...rise(reduce, 2)} className="mx-auto mt-4 max-w-[300px] text-[12px] font-medium leading-[1.8] tracking-[0.04em] text-ivory [text-shadow:0_0_14px_rgba(10,18,22,0.85),0_1px_2px_rgba(10,18,22,0.6)] sm:max-w-none sm:text-[13px]" data-testid="connect-services">
      Yoga • Meditation • Breathwork • Bodywork • Massage • Hypnotherapy
    </motion.p>
    <motion.p {...rise(reduce, 3)} className="mt-6 text-[15px] font-normal tracking-[0.05em] text-ivory [text-shadow:0_0_16px_rgba(10,18,22,0.9),0_1px_2px_rgba(10,18,22,0.6)] sm:text-base" data-testid="connect-tagline">
      Move Better. Feel Better. Be Present.
    </motion.p>
  </header>
);

const Thumb = ({ card }) => (
  <span className="h-11 w-11 shrink-0 overflow-hidden rounded-[10px] bg-sand">
    <img
      src={card.thumb || src(card.img.p, 500)}
      alt=""
      loading={card.featured ? "eager" : "lazy"}
      decoding="async"
      width="44"
      height="44"
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
      style={{ objectPosition: card.pos || card.img.pos }}
    />
  </span>
);

const LinkCard = ({ card, i, reduce }) => (
  <motion.li {...rise(reduce, i + 4)}>
    <Link
      to={card.to}
      data-testid={`connect-link-${card.id}`}
      className={`group flex min-h-[56px] items-center gap-3 rounded-[14px] border p-1.5 pr-2.5 transition-[background-color,border-color,box-shadow,transform] duration-300 active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-ink focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
        card.featured
          ? "border-ocean-deep bg-ocean-deep text-ivory shadow-[0_14px_30px_-20px_rgba(29,54,64,0.9)] hover:bg-[#16292F]"
          : "border-white/70 bg-white/85 text-ink shadow-[0_10px_24px_-22px_rgba(43,38,32,0.6)] backdrop-blur-md hover:border-coral/40 hover:bg-white/95"
      }`}
    >
      <Thumb card={card} />
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px] font-semibold leading-[1.2] tracking-[-0.005em] max-[379px]:text-[13.5px]">{card.title}</span>
        <span className={`mt-0.5 line-clamp-2 block text-[12px] leading-[1.3] max-[379px]:text-[11px] ${card.featured ? "text-ivory/85" : "text-ink/80"}`}>{card.desc}</span>
      </span>
      <span aria-hidden className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-[transform,background-color] duration-300 group-hover:translate-x-0.5 max-[379px]:h-6 max-[379px]:w-6 ${card.featured ? "bg-ivory/10 text-coral-soft" : "bg-ink/[0.04] text-coral-ink group-hover:bg-coral/10"}`}>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
      </span>
    </Link>
  </motion.li>
);

const pill = "inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-ink/10 bg-white/45 px-3.5 text-[13px] font-medium text-ink backdrop-blur-sm transition-[background-color,color,border-color] duration-300 hover:border-ink hover:bg-ink hover:text-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-ink";

const StayConnected = () => (
  <section aria-labelledby="connect-stay-heading" className="mt-16 text-center" data-testid="connect-stay">
    <h2 id="connect-stay-heading" className="text-[10px] font-semibold uppercase tracking-[0.3em] text-ink-soft">
      Stay Connected
    </h2>
    <ul className="mt-4 flex flex-wrap justify-center gap-2">
      {SOCIAL.map(({ id, label, href, Icon }) => (
        <li key={id}>
          <a href={href} target="_blank" rel="noopener noreferrer" className={pill} data-testid={`connect-social-${id}`}>
            <Icon aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {label}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </li>
      ))}
    </ul>
  </section>
);

const Press = () => (
  <section aria-label="As seen on" className="mx-auto mt-12 max-w-[440px] rounded-2xl bg-white/45 px-3 pb-3 pt-4 backdrop-blur-sm" data-testid="connect-press">
    <p className="mb-2 text-center text-[9.5px] font-semibold uppercase tracking-[0.32em] text-ink-soft">As seen on</p>
    <PressMarquee names={PRESS} compact />
  </section>
);

const MiniFooter = () => (
  <footer className="pb-14 pt-12 text-center" data-testid="connect-footer">
    <a
      href="https://jakeferree.com"
      aria-label="Feeling Moved — jakeferree.com"
      className="group inline-flex min-h-[44px] flex-col items-center rounded-xl px-5 py-3 transition-[transform,opacity] duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] active:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-ink focus-visible:ring-offset-2 focus-visible:ring-offset-[#EEE4D6]"
      data-testid="connect-footer-logo-link"
    >
      <img src={LOGO} alt="" width="1138" height="183" loading="lazy" className="h-[38px] w-auto" data-testid="connect-footer-logo" />
      <span className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.22em] text-ink-soft transition-colors duration-300 group-hover:text-ink" data-testid="connect-footer-visit">
        Visit Website
        <span aria-hidden className="inline-block transition-transform duration-300 group-hover:-translate-y-px group-hover:translate-x-0.5">↗</span>
      </span>
    </a>
  </footer>
);

export default function Connect() {
  const reduce = useReducedMotion();
  usePageMeta(
    "Jake Ferree — Connect | Feeling Moved",
    "Connect with Jake Ferree: the Yoga Sutras app, online practice, this week's classes, private sessions, massage & bodywork, retreats and more."
  );
  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#EEE4D6] text-ink" data-testid="connect-page">
      <div aria-hidden className="connect-field" data-testid="connect-background" />
      <OceanBand reduce={reduce} />
      <Hero reduce={reduce} />
      <main className="relative z-10 mx-auto w-full max-w-[480px] px-5 sm:px-6" data-testid="connect-main">
        <nav aria-label="Explore Feeling Moved" className="mt-14 sm:mt-16">
          <ul className="space-y-3.5" data-testid="connect-links">
            {LINK_CARDS.map((c, i) => (
              <LinkCard key={c.id} card={c} i={i} reduce={reduce} />
            ))}
          </ul>
        </nav>
        <StayConnected />
        <Press />
        <MiniFooter />
      </main>
    </div>
  );
}
